import { StandingOrderBehavior, UnitType } from '@/graphql/types/generated';
import {
  hasMarketSettings,
  WarehousesRow,
} from '@/components/supplyItems/types/WarehouseRows';
import { standingOrdersOn } from '@/components/marketOrderingSettings';

/**
 * A lifetime limit on how much of an item (or its sample) one location can ever order, across all of
 * its orders. Empty means no limit.
 *
 * The item mutations treat the two empty values differently: an omitted field keeps the stored limit,
 * null removes it. So the form keeps them apart too: a cleared input is null (see
 * {@link parseMaxQuantityInput}), while undefined only means the form never had the field.
 */

/** Form value → mutation input: undefined keeps the stored limit, null removes it. */
export const toMaxQuantityInput = (
  value: unknown,
): number | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;
  return Number(value);
};

/** Field `parse`: a cleared input means "no limit", not "leave as is". */
export const parseMaxQuantityInput = (value: unknown) =>
  value === '' || value === undefined ? null : value;

/** Field validator: empty is allowed; anything else has to be a whole number of at least 1. */
export const validateMaxQuantity = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const limit = Number(value);
  return Number.isInteger(limit) && limit >= 1
    ? undefined
    : 'Must be a whole number of at least 1';
};

/**
 * IMS refuses a lifetime limit on an item available for standing orders: once a location reached the
 * limit, the item would drop off its standing orders without anyone being told. The sample's limit
 * isn't affected, since samples can't be put on standing orders.
 */
export const standingOrdersConflict =
  "This item is available for standing orders, so it can't have a Lifetime Limit per Location. Clear the limit, or turn standing orders off.";

/**
 * Whether the item is available for standing orders, the way IMS decides it: in any market it's sold
 * in while markets set their own (the market item flags), otherwise its own setting.
 */
export const standingOrdersEnabled = (
  values: {
    standingOrderBehavior?: StandingOrderBehavior | null;
    uiWarehouseRows?: WarehousesRow[];
  },
  marketItemFlags: boolean,
): boolean =>
  marketItemFlags
    ? (values.uiWarehouseRows ?? [])
        .filter(hasMarketSettings)
        .some(standingOrdersOn)
    : values.standingOrderBehavior === StandingOrderBehavior.Available;

/** Field validator for the item's limit: {@link validateMaxQuantity}, and no limit with standing orders on. */
export const validateItemMaxQuantity = (
  value: unknown,
  standingOrders: boolean,
): string | undefined => {
  const error = validateMaxQuantity(value);
  if (error) return error;
  if (value === null || value === undefined || value === '') return undefined;
  return standingOrders ? standingOrdersConflict : undefined;
};

/**
 * Field validator for the sample's limit: the item rules, plus a sample unit, since IMS refuses a
 * sample limit for an item without one.
 */
export const validateSampleMaxQuantity = (
  value: unknown,
  sampleUnitId: string | null | undefined,
): string | undefined => {
  const error = validateMaxQuantity(value);
  if (error) return error;
  if (value === null || value === undefined || value === '') return undefined;
  return sampleUnitId ? undefined : 'Choose a sample sales unit first';
};

/** The parts of the item's sale unit that decide whether its purchase multiple applies. */
export type SaleUnitForLimit = {
  unitType?: UnitType | null;
  conversionRate?: number | null;
};

/**
 * Orders for an item with a purchase multiple come in that multiple, so only the largest multiple at
 * or below the limit can ever be reached. Returns a warning when the two don't line up; the limit is
 * still valid.
 *
 * An item sold by the case (a CASE sale unit holding more than one) is ordered in cases, and so-api
 * ignores its purchase multiple, so no warning applies to it.
 */
export const maxQuantityMultipleWarning = (
  limit: unknown,
  purchaseMultiple: unknown,
  saleUnit?: SaleUnitForLimit,
): string | undefined => {
  if (
    saleUnit?.unitType === UnitType.Case &&
    Number(saleUnit.conversionRate) > 1
  ) {
    return undefined;
  }
  if (
    validateMaxQuantity(limit) ||
    limit === null ||
    limit === undefined ||
    limit === ''
  ) {
    return undefined;
  }
  const max = Number(limit);
  const multiple = Number(purchaseMultiple);
  if (!Number.isInteger(multiple) || multiple <= 1 || max % multiple === 0)
    return undefined;

  const reachable = Math.floor(max / multiple) * multiple;
  return reachable === 0
    ? `Orders come in multiples of ${multiple}, so a limit of ${max} means this item can't be ordered at all.`
    : `Orders come in multiples of ${multiple}, so a location can only reach ${reachable} of this limit.`;
};

/** Read-only display of a limit. */
export const formatMaxQuantity = (value: number | null | undefined) =>
  value === null || value === undefined ? 'No limit' : String(value);
